import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAIService } from '@/context/AIServiceContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import RiskAnalysis from '@/components/Insights/RiskAnalysis';
import sanitizeText from '@/utils/sanitizeText';
import * as Sentry from '@sentry/react';

// Increased retry delay to 120 seconds for better rate limit handling
const RETRY_DELAY_MS = 120000; // 120s (increased from 60s)
const MAX_RETRIES = 3;

const RiskRegister = () => {
  const { apiFetch } = useAuth();
  const { aiEnabled, isLoading: aiLoading } = useAIService();
  const [risks, setRisks] = useState([]); // start empty so placeholders display if none fetched
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const retryTimeoutRef = useRef(null);
  const attemptRef = useRef(0);

  // Cleanup retry timer on unmount
  useEffect(() => () => { if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current); }, []);

  // Fetch risk register only when AI is enabled (from context)
  useEffect(() => {
    // Wait for AI config to load
    if (aiLoading) return;
    
    // If AI is disabled, don't fetch and stop loading
    if (!aiEnabled) {
      setLoading(false);
      return;
    }

    let active = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await apiFetch('/api/insights/it-risk-register');
        if (res.status === 204) { if (active) { setRisks([]); attemptRef.current = 0; } return; }
        if (res.status === 429) throw new Error('RATE_LIMIT');
        if (!res.ok) throw new Error('Failed to load risk register');
        const json = await res.json();
        if (active) {
          if (Array.isArray(json.risks) && json.risks.length) {
            setRisks(json.risks.map(r => ({
              ...r,
              risk: sanitizeText(r.risk),
              description: sanitizeText(r.description),
              impact: sanitizeText(r.impact),
              likelihood: sanitizeText(r.likelihood),
              score: sanitizeText(r.score),
              mitigation: sanitizeText(r.mitigation),
              owner: sanitizeText(r.owner),
              status: sanitizeText(r.status),
            })));
          } else {
            setRisks([]);
          }
          attemptRef.current = 0;
        }
      } catch (e) {
          Sentry.captureException(e);
        if (!active) return;
        const msg = e.message || 'Error';
        if (msg === 'RATE_LIMIT' && attemptRef.current < MAX_RETRIES) {
          const attempt = ++attemptRef.current;
          setError(`Service busy (429). Retrying in 2 minutes (attempt ${attempt}/${MAX_RETRIES})`);
          retryTimeoutRef.current = setTimeout(() => setReloadToken(t => t + 1), RETRY_DELAY_MS);
        } else {
          setError(msg === 'RATE_LIMIT' ? 'Service busy. Please try again later.' : msg);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [aiEnabled, aiLoading, apiFetch, reloadToken]);

  const showPlaceholders = !loading && (!risks || risks.length === 0);

  return (
    <div className="dashboard-card risk-register" style={{ padding: 16 }}>
      <div className="risk-register-body">
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>IT Risk Register</h2>
        {loading && <HexagonLoader />}
        {error && !loading && <div style={{ color: error.startsWith('Service busy') ? '#b36b00' : 'red', marginBottom: 8 }}>Risk Register: {error}</div>}
        <table className="data-table risk-register-table">
          <thead>
            <tr>
              <th>Risk</th>
              <th>Description</th>
              <th>Impact</th>
              <th>Likelihood</th>
              <th>Risk Score</th>
              <th>Mitigation / Control</th>
              <th>Owner</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {showPlaceholders && Array.from({ length: 10 }).map((_, i) => (
              <tr key={i}>
                <td style={{ height: 28 }}>&nbsp;</td>
                <td style={{ textAlign: 'left' }}>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td style={{ textAlign: 'left' }}>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            ))}
            {!showPlaceholders && risks.map((r, i) => (
              <tr key={r.id || i}>
                <td>{r.risk}</td>
                <td style={{ textAlign: 'left' }}>{r.description}</td>
                <td>{r.impact}</td>
                <td>{r.likelihood}</td>
                <td>{r.score}</td>
                <td style={{ textAlign: 'left' }}>{r.mitigation}</td>
                <td>{r.owner}</td>
                <td>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI Risk Analysis - between table and sign-off */}
      <RiskAnalysis />

      {/* Signature / Approval Block */}
      <div className="risk-register-signature" style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid #ccc' }}>
        <div className="sig-block">
          <div className="sig-approval-title" style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Systems Operations Engineer Approval</div>
          <div className="sig-row" style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', minWidth: 200 }}>
              <div className="sig-line" style={{ height: 40, borderBottom: '1px solid #000' }} />
              <div className="sig-label" style={{ fontSize: 11, marginTop: 4 }}>Name (Print)</div>
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 200 }}>
              <div className="sig-line" style={{ height: 40, borderBottom: '1px solid #000' }} />
              <div className="sig-label" style={{ fontSize: 11, marginTop: 4 }}>Signature</div>
            </div>
            <div style={{ flex: '0 1 160px', minWidth: 140 }}>
              <div className="sig-line" style={{ height: 40, borderBottom: '1px solid #000' }} />
              <div className="sig-label" style={{ fontSize: 11, marginTop: 4 }}>Date</div>
            </div>
          </div>
        </div>
        <div className="sig-block" style={{ marginTop: 16 }}>
          <div className="sig-approval-title" style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Systems & Integrations Manager Approval</div>
          <div className="sig-row" style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', minWidth: 200 }}>
              <div className="sig-line" style={{ height: 40, borderBottom: '1px solid #000' }} />
              <div className="sig-label" style={{ fontSize: 11, marginTop: 4 }}>Name (Print)</div>
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 200 }}>
              <div className="sig-line" style={{ height: 40, borderBottom: '1px solid #000' }} />
              <div className="sig-label" style={{ fontSize: 11, marginTop: 4 }}>Signature</div>
            </div>
            <div style={{ flex: '0 1 160px', minWidth: 140 }}>
              <div className="sig-line" style={{ height: 40, borderBottom: '1px solid #000' }} />
              <div className="sig-label" style={{ fontSize: 11, marginTop: 4 }}>Date</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskRegister;
